import React, { FC, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import {
	videoProviderErrorMessage,
	videoProvidersUpdate,
} from '../Slices/VideoProviderSettings.slice';
import { VideoProviderSetting } from '../Types/VideoProviders.types';
import {
	buildCredentialsPatch,
	CardNote,
	emptySecretDraft,
	ProviderCard,
	SecretDraft,
	SecretField,
	storedSecret,
} from './ProviderCardShared';

type Props = {
	setting: VideoProviderSetting;
};

const VdocipherCard: FC<Props> = ({ setting }) => {
	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const { loaders } = useTypedSelector((state) => state.VideoProviderSettings);

	const [enabled, setEnabled] = useState(setting.enabled);
	const [secretDraft, setSecretDraft] = useState<SecretDraft>(
		emptySecretDraft()
	);
	const [apiHost, setApiHost] = useState('');
	const [otpTtl, setOtpTtl] = useState('');
	const [allowedHrefs, setAllowedHrefs] = useState('');

	useEffect(() => {
		setEnabled(setting.enabled);
		setSecretDraft(emptySecretDraft());
		setApiHost(setting.config?.api_host ?? '');
		setOtpTtl(
			setting.config?.otp_ttl != null ? String(setting.config.otp_ttl) : ''
		);
		setAllowedHrefs(setting.config?.allowed_hrefs ?? '');
	}, [setting]);

	const save = async () => {
		const ttl = otpTtl.trim() === '' ? null : Number(otpTtl);
		if (ttl !== null && (!Number.isFinite(ttl) || ttl <= 0)) {
			displaySnackbar('error', 'OTP TTL must be a positive number of seconds');
			return;
		}
		try {
			await dispatch(
				videoProvidersUpdate({
					provider: 'vdocipher',
					enabled,
					credentials: buildCredentialsPatch({ api_secret: secretDraft }),
					config: {
						api_host: apiHost.trim() || null,
						otp_ttl: ttl,
						allowed_hrefs: allowedHrefs.trim() || null,
					},
				})
			).unwrap();
			displaySnackbar('success', 'VdoCipher settings saved');
		} catch (error: any) {
			displaySnackbar(
				'error',
				videoProviderErrorMessage(error, 'Unable to save VdoCipher settings')
			);
		}
	};

	return (
		<ProviderCard
			title="VdoCipher"
			subtitle="Nested folders: instructor → product → course/lecture"
			enabled={enabled}
			onEnabledChange={setEnabled}
			onSave={save}
			saving={loaders.update === 'pending'}
		>
			<SecretField
				label="API secret"
				stored={storedSecret(setting.credentials, 'api_secret')}
				draft={secretDraft}
				onChange={setSecretDraft}
				helperText="Needs upload + folder permissions"
			/>
			<TextField
				size="small"
				fullWidth
				label="API host"
				placeholder="https://dev.vdocipher.com"
				value={apiHost}
				onChange={(event) => setApiHost(event.target.value)}
			/>
			<TextField
				size="small"
				fullWidth
				type="number"
				inputMode="numeric"
				label="OTP TTL (seconds)"
				value={otpTtl}
				onChange={(event) => setOtpTtl(event.target.value)}
				helperText="Playback-token redeem window. Empty = default (600s)."
			/>
			<TextField
				size="small"
				fullWidth
				label="Allowed domains"
				value={allowedHrefs}
				onChange={(event) => setAllowedHrefs(event.target.value)}
			/>
			{/* Plain-flow note instead of TextField helperText: the theme
			    positions helper text absolutely, so it overlaps whatever
			    element follows the field. */}
			<CardNote>
				whitelisthref pattern per VdoCipher docs. Empty = no domain lock.
			</CardNote>
			<CardNote>
				OTP TTL and allowed domains moved here from App Settings — the student
				backend reads them from this table (with the old values as fallback).
			</CardNote>
		</ProviderCard>
	);
};

export default VdocipherCard;
